import React, { useEffect, useMemo, useState } from "react";
import { Button, Drawer, Layout, Menu } from "antd";
import { CloseOutlined, MenuOutlined } from "@ant-design/icons";
import { dashboardMenus, findMenuParentKey } from "./MainPage.helper";
import LogoDark from "../../assets/logo/nendaxpay-logo-dark.png";
import LogoLight from "../../assets/logo/nendaxpay-logo-light.png";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { getFirstSegment } from "../../utils/utils";
import { ThemeContext } from "../../context/ThemeContext";
import HeaderActions from "../../ui/Header/HeaderActions";
const { Content, Sider } = Layout;

const MainPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [theme, setTheme] = useState(() => localStorage.getItem("nendax-admin-theme") || "light");
  const pathName = location.pathname.toLowerCase();
  // const { Dashboard } = MENU_KEYS;

  const selected = pathName.substring(11)
    ? pathName.substring(11)
    : "dashboard";
  
  const getMenuItems = useMemo(() => {
    if (selected) {
      const menus = dashboardMenus(selected.toLowerCase());
      return menus;
    }
    return [];
  }, [selected]);

  const selectedKey = getFirstSegment(selected);
  const defaultOpenKeys = useMemo(
    () => [findMenuParentKey(getMenuItems, selectedKey)].filter(Boolean),
    [getMenuItems, selectedKey]
  );

  useEffect(() => {
    document.body.classList.toggle("nendax-admin-dark", theme === "dark");
    document.body.classList.toggle("nendax-admin-light", theme !== "dark");

    return () => {
      document.body.classList.remove("nendax-admin-dark", "nendax-admin-light");
    };
  }, [theme]);

  const menu = (
    <Menu
      mode="inline"
      className="MainPage__menu"
      defaultOpenKeys={defaultOpenKeys}
      items={getMenuItems}
      selectedKeys={[selected, selectedKey]}
      onClick={(event) => {
        navigate(`/dashboard/${event.key?.toLowerCase()}`);
        setIsMobileMenuOpen(false);
      }}
    />
  );

  const toggleTheme = () => {
    const nextTheme = theme === "dark" ? "light" : "dark";
    setTheme(nextTheme);
    localStorage.setItem("nendax-admin-theme", nextTheme);
  };

  const goToDashboard = () => {
    navigate("/dashboard/overview");
    setIsMobileMenuOpen(false);
  };

  const logo = (
    <button
      aria-label="Go to dashboard"
      className="sapa-menu__logo"
      type="button"
      onClick={goToDashboard}
    >
      <img src={theme === "dark" ? LogoDark : LogoLight} alt="NendaxPay Admin" />
      <span>Admin</span>
    </button>
  );

  return (
    <Content className="MainPage" data-theme={theme}>
      <ThemeContext.Provider value={{ theme, toggleTheme }}>
      <Layout className="MainPage__layout">
        <Sider className="MainPage__side" breakpoint="lg">
          {logo}
          {menu}
        </Sider>
        <div className="MainPage__mobileTopbar">
          <div className="MainPage__mobileActions">
            <Button
              aria-label="Open navigation menu"
              className="MainPage__menuButton"
              icon={<MenuOutlined />}
              type="text"
              onClick={() => setIsMobileMenuOpen(true)}
            />
          </div>
          {logo}
          <HeaderActions />
        </div>
        <Drawer
          className="MainPage__mobileDrawer"
          open={isMobileMenuOpen}
          placement="left"
          width="min(84vw, 320px)"
          closable={false}
          onClose={() => setIsMobileMenuOpen(false)}
        >
          <div className="MainPage__drawerHeader">
            {logo}
            <Button
              aria-label="Close navigation menu"
              className="MainPage__menuButton"
              icon={<CloseOutlined />}
              type="text"
              onClick={() => setIsMobileMenuOpen(false)}
            />
          </div>
          {menu}
        </Drawer>
        <Content className="MainPage__content">
          <Outlet />
        </Content>
      </Layout>
      </ThemeContext.Provider>
    </Content>
  );
};

export default MainPage;
